import { useEffect, useState } from "react";
import { apiFetch } from "./viewer";

/**
 * "My past stories": every run this viewer has finished, newest first.
 *
 * Signed in, that is everything on the account plus anything made in this browser before signing in. As a
 * guest it is this browser's runs, which is exactly the argument for signing in - so an empty or
 * cookie-bound list says so rather than pretending there is nothing to see.
 */

type Story = {
  nodeId: string;
  createdAt: string;
  outcome: string;
  summary: string | null;
  film: boolean;
  thumbnail: string | null;
};

const when = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

export function MyStories({ onClose }: { onClose: () => void }) {
  const [stories, setStories] = useState<Story[] | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch("/api/my-stories")
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(out => {
        setStories(out.stories ?? []);
        setSignedIn(Boolean(out.signedIn));
      })
      .catch(err => setError((err as Error).message));
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="absolute inset-0 z-[68] flex flex-col bg-black/85 backdrop-blur-sm">
      <div className="flex items-center justify-between gap-4 border-b border-white/10 px-5 py-4">
        <div>
          <div className="font-display text-xs font-semibold tracking-[0.35em] text-teal">MY PAST STORIES</div>
          <div className="mt-1 font-mono text-[11px] text-white/40">
            {stories === null ? "Looking them up…" : `${stories.length} finished ${stories.length === 1 ? "story" : "stories"}`}
            {stories !== null && !signedIn && stories.length > 0 && " · on this browser only"}
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded border border-white/20 px-3 py-1.5 font-mono text-[11px] tracking-widest text-white/60 transition hover:border-sodium hover:text-sodium"
        >
          CLOSE
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain p-5">
        {error && <div className="font-mono text-xs text-siren-red">Couldn't load your stories: {error}</div>}

        {stories !== null && stories.length === 0 && (
          <p className="mx-auto mt-16 max-w-sm text-center font-mono text-xs leading-relaxed text-white/45">
            No finished stories yet. Direct Sloppy Joe until he escapes or gets caught, and the run will appear here.
            {!signedIn && " Sign in to keep them across devices."}
          </p>
        )}

        {/* Same card grid as the admin film wall, minus anything an operator would care about. */}
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(min(260px,100%),1fr))]">
          {(stories ?? []).map(story => (
            <a
              key={story.nodeId}
              href={`/s/${story.nodeId}`}
              target="_blank"
              rel="noreferrer"
              className="group overflow-hidden rounded-lg border border-white/10 bg-white/[0.03] transition hover:border-teal/60"
            >
              <div className="relative aspect-video bg-black">
                {story.thumbnail ? (
                  <img src={story.thumbnail} alt="" loading="lazy" className="size-full object-cover opacity-90 transition group-hover:opacity-100" />
                ) : (
                  <div className="grid size-full place-items-center font-mono text-[10px] text-white/25">
                    {story.film ? "FILM READY" : "NO FILM SAVED"}
                  </div>
                )}
                <span
                  className={`absolute right-2 top-2 rounded border px-1.5 py-0.5 font-mono text-[9px] tracking-widest ${
                    story.outcome === "escaped" ? "border-teal/70 text-teal" : "border-siren-red/70 text-siren-red"
                  }`}
                >
                  {story.outcome === "escaped" ? "ESCAPED" : "CAUGHT"}
                </span>
              </div>
              <div className="p-3">
                <div className="font-mono text-[10px] tracking-widest text-white/35">{when(story.createdAt)}</div>
                {story.summary && <p className="mt-1 line-clamp-3 font-mono text-[11px] leading-relaxed text-white/60">{story.summary}</p>}
                <div className="mt-2 font-mono text-[10px] tracking-widest text-teal underline underline-offset-4">WATCH / SHARE →</div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
